import React, { useEffect, useRef, useState } from 'react';
import { REPORT_TERMS_PATH } from '../shared/homeHistoryReport';
import { REPORT_FOOTER_SHORT } from '../shared/reportLegalCopy';
import { PACARION_POSTAL_ADDRESS, PACARION_TAGLINE, PACARION_WORDMARK } from '../shared/sponsorlessBrand';
import { openExternalUrl } from '../utils/openExternal';

const LOGO = `${process.env.PUBLIC_URL || ''}/images/logo/pacarion-logo-light.png`;
const PAPER_FONT = 'Helvetica, "Liberation Sans", Arial, sans-serif';
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;

export function ReportWordmark() {
  return (
    <img
      src={LOGO}
      alt={PACARION_WORDMARK}
      style={{ display: 'block', width: '168px', maxWidth: '70%', height: 'auto' }}
    />
  );
}

export function ReportFooter({ preparedLabel }) {
  const generated = preparedLabel ? `Generated ${preparedLabel}` : '';
  return (
    <footer
      className="pacarion-report-footer"
      style={{
        marginTop: '1.5rem',
        padding: '1.25rem 1.1rem 1.35rem',
        background: '#0f172a',
        color: '#e2e8f0',
        borderRadius: '16px',
        textAlign: 'center',
      }}
    >
      <p style={{ margin: '0 0 0.35rem', fontSize: '0.82rem', fontWeight: 650, lineHeight: 1.45, color: '#fff' }}>
        {REPORT_FOOTER_SHORT}
      </p>
      <p style={{ margin: '0 0 0.55rem', fontSize: '0.92rem', color: '#cbd5e1' }}>{PACARION_TAGLINE}</p>
      <p style={{ margin: 0, fontSize: '0.82rem', lineHeight: 1.55 }}>
        {[generated, 'Pacarion LLC', PACARION_POSTAL_ADDRESS].filter(Boolean).join(' · ')}
      </p>
    </footer>
  );
}

function fileLinksFrom(files) {
  const links = {};
  const map = files || {};
  Object.keys(map).forEach((id) => {
    const file = map[id];
    if (file && file.url) links[id] = { url: file.url, name: file.name || 'Open file' };
  });
  return links;
}

function PaperOp({ op, pageHeight }) {
  if (!op) return null;
  if (op.type === 'rect') {
    return (
      <div
        style={{
          position: 'absolute',
          left: op.x,
          top: pageHeight - op.y - op.height,
          width: op.width,
          height: op.height,
          background: op.fill || 'transparent',
          border: op.stroke ? `1px solid ${op.stroke}` : 'none',
          boxSizing: 'border-box',
        }}
      />
    );
  }
  if (op.type === 'circle') {
    return (
      <span
        style={{
          position: 'absolute',
          left: op.x - op.size,
          top: pageHeight - op.y - op.size * 0.8,
          width: op.size * 2,
          height: op.size * 2,
          borderRadius: '50%',
          background: op.color,
        }}
      />
    );
  }
  if (op.type === 'line') {
    const top = pageHeight - Math.max(op.y1, op.y2);
    return (
      <span
        style={{
          position: 'absolute',
          left: op.x - op.thickness / 2,
          top: top,
          width: op.thickness,
          height: Math.max(1, Math.abs(op.y2 - op.y1)),
          background: op.color,
        }}
      />
    );
  }
  if (op.type === 'image') {
    const src = op.name === 'logo' ? LOGO : op.url;
    if (!src) return null;
    return (
      <img
        src={src}
        alt={op.name === 'logo' ? PACARION_WORDMARK : ''}
        style={{
          position: 'absolute',
          left: op.x,
          top: pageHeight - op.y - op.height,
          width: op.width,
          height: op.height,
          objectFit: 'contain',
        }}
      />
    );
  }
  if (op.type !== 'text' || !op.text) return null;
  const style = {
    position: 'absolute',
    left: op.x,
    top: pageHeight - op.y - op.size,
    fontSize: op.size,
    lineHeight: op.size + 'px',
    fontWeight: op.font === 'bold' ? 700 : 400,
    color: op.color || '#0f172a',
    whiteSpace: 'pre',
    fontFamily: PAPER_FONT,
    textDecoration: 'none',
  };
  if (op.href) {
    const external = op.href.indexOf('/report-terms') !== 0;
    return (
      <a
        href={op.href.indexOf('/report-terms') === 0 ? REPORT_TERMS_PATH : op.href}
        style={style}
        onClick={external ? (event) => {
          event.preventDefault();
          openExternalUrl(op.href);
        } : undefined}
      >
        {op.text}
      </a>
    );
  }
  return <span style={style}>{op.text}</span>;
}

function PaperSheet({ page, scale }) {
  return (
    <div style={{ width: '100%', height: PAGE_HEIGHT * scale, overflow: 'hidden', background: '#ffffff' }}>
      <div
        className="pacarion-report-paper"
        style={{
          width: PAGE_WIDTH,
          height: PAGE_HEIGHT,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'relative',
          background: '#ffffff',
          color: '#0f172a',
          colorScheme: 'light',
          fontFamily: PAPER_FONT,
        }}
      >
        {(page.ops || []).map((op, index) => (
          <PaperOp key={index} op={op} pageHeight={page.height || PAGE_HEIGHT} />
        ))}
      </div>
    </div>
  );
}

/**
 * The in-app preview and the share page use the same page layout as the PDF.
 */
export default function HomeHistoryReportBody({ snapshot, files, logoUrl, headshotUrl, expiresLabel, termsUrl }) {
  const frameRef = useRef(null);
  const [pages, setPages] = useState(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!snapshot) return undefined;
    let cancel = false;
    import('../shared/reportPdf').then((mod) => {
      const layout = mod.reportPaperPages;
      return layout(snapshot, {
        logoUrl: logoUrl || '',
        headshotUrl: headshotUrl || '',
        expiresLabel: expiresLabel || '',
        termsUrl: termsUrl || '',
        fileLinks: fileLinksFrom(files),
      });
    }).then((next) => {
      if (!cancel) setPages(next || []);
    }).catch(() => {
      if (!cancel) setPages([]);
    });
    return () => {
      cancel = true;
    };
  }, [snapshot, files, logoUrl, headshotUrl, expiresLabel, termsUrl]);

  useEffect(() => {
    function measure() {
      const width = frameRef.current && frameRef.current.clientWidth;
      if (width > 0) setScale(width / PAGE_WIDTH);
    }
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [pages]);

  if (!snapshot) return null;
  return (
    <div
      ref={frameRef}
      data-testid="home-history-report-body"
      className="pacarion-report-paper"
      style={{
        width: '100%',
        maxWidth: PAGE_WIDTH,
        margin: '0 auto',
        background: '#ffffff',
        color: '#0f172a',
        colorScheme: 'light',
        fontFamily: PAPER_FONT,
      }}
    >
      <style>{`
        .pacarion-report-paper, .pacarion-report-paper * {
          font-family: Helvetica, "Liberation Sans", Arial, sans-serif;
          color-scheme: light;
        }
      `}</style>
      {pages && pages.length ? pages.map((page, index) => (
        <PaperSheet key={index} page={page} scale={scale} />
      )) : (
        <div style={{ minHeight: 120, padding: 24, color: '#0f172a', background: '#ffffff' }}>
          Preparing the page…
        </div>
      )}
    </div>
  );
}
